// =============================================================================
// Portal My Product Detail — read-only view + apparel render observability
// -----------------------------------------------------------------------------
// Two row classes share this page:
//
//   • TEMPLATE_PUBLISH — published to a connected store. Shows the
//     classic variant pricing table + "Open in Shopify admin" deep
//     link. Render observability is hidden (these rows never run
//     through the apparel render pipeline).
//
//   • DESIGN_GENERATED — merchant-authored apparel designs. Shows a
//     visual variant grid driven by the apparel render pipeline. The
//     page polls every 5 s while any variant is mid-render; once
//     everything settles polling drops to 60 s. A "Re-render preview"
//     CTA appears when any variant or canonical side ended in FAILED.
//
// PR-C adds:
//   • "Publish to Shopify" CTA for unpublished DESIGN_GENERATED rows
//   • Drift banner + "Re-publish to Shopify" for rows where the
//     bound design diverged from the published variant images
//     (Product.requiresRepublish === true)
// =============================================================================

import { useParams, useNavigate, Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardHeader, Badge, Spinner } from '@tkpod/ui';
import { useApiClient } from '@tkpod/api-client';
import type {
  EffectiveFulfillmentSettings,
  FulfillmentLane,
} from '@tkpod/shared';
import {
  parseMoneyInput,
  formatMoneyValue,
  adjustPrice,
  isBelowCost,
  computeProfit,
  optionValueByAxisHints,
  SIZE_AXIS_HINTS,
  countOptionAxes,
  variantRowLabel,
  resolveTableHeroPreview,
  type PriceBulkOp,
  type PriceBulkUnit,
} from '@tkpod/shared';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { generateUuidV4 } from '../lib/uuid';
import { GroupedVariantTable } from '../components/GroupedVariantTable';
import { VariantBulkPriceModal } from '../components/VariantBulkPriceModal';
import { ProductPreviewGallery } from '../components/ProductPreviewGallery';

const STATUS_VARIANT: Record<
  string,
  'primary' | 'default' | 'success' | 'warning' | 'danger' | 'info'
> = {
  DRAFT: 'default',
  ACTIVE: 'success',
  ARCHIVED: 'default',
  SYNC_ERROR: 'danger',
};

const SYNC_VARIANT: Record<
  string,
  'primary' | 'default' | 'success' | 'warning' | 'danger' | 'info'
> = {
  PENDING: 'default',
  SYNCING: 'info',
  SYNCED: 'success',
  ERROR: 'danger',
  SYNC_REQUIRED: 'warning',
  // Legacy status (pre full-sync model); resolves via "Sync from Shopify".
  DRIFT_DETECTED: 'warning',
};

/** Merchant-friendly sync badge labels (raw enum words are unclear). */
const SYNC_LABEL: Record<string, string> = {
  PENDING: 'Pending',
  SYNCING: 'Syncing',
  SYNCED: 'Synced',
  ERROR: 'Error',
  SYNC_REQUIRED: 'Sync required',
  DRIFT_DETECTED: 'Sync required',
};

const PREVIEW_JOB_VARIANT: Record<
  string,
  'primary' | 'default' | 'success' | 'warning' | 'danger' | 'info'
> = {
  PENDING: 'default',
  RUNNING: 'info',
  DONE: 'success',
  FAILED: 'danger',
};

interface RenderSummary {
  total: number;
  pending: number;
  running: number;
  done: number;
  failed: number;
}

interface ProductVariantRow {
  id: string;
  title: string | null;
  internalSku: string;
  externalSku: string | null;
  externalVariantId: string | null;
  price: string;
  cost: string | null;
  inventoryQuantity: number;
  options: unknown;
  // Apparel multi-side v2: per-variant render artefacts.
  imageUrl: string | null;
  previewJobStatus: 'PENDING' | 'RUNNING' | 'DONE' | 'FAILED';
  blankVariantId: string | null;
  /**
   * Sanitized CSS hex from BlankVariant.TemplateColor. Optional so older
   * API responses keep parsing; gallery swatches fall back to a label.
   */
  colorHex?: string | null;
  /**
   * Full-sync model: set when the variant was removed on the Shopify store.
   * Removed variants are shown greyed out, excluded from pricing edits, and
   * blocked from new orders server-side.
   */
  externalDeletedAt?: string | null;
  /**
   * Per-side preview rows. Ordered to mirror `MyProductDetail.printSides`.
   * `previewUrl` is null while the render is still queued or has failed.
   */
  sides: Array<{
    printAreaId: string;
    slug: string;
    previewStatus: 'PENDING' | 'QUEUED' | 'READY' | 'FAILED';
    previewUrl: string | null;
  }>;
}

interface PrintSideMeta {
  printAreaId: string;
  slug: string;
  displayName: string;
  isPrimary: boolean;
  sortOrder: number;
  exportStatus: 'PENDING' | 'QUEUED' | 'READY' | 'FAILED';
  exportUrl: string | null;
}

interface MyProductDetail {
  id: string;
  title: string;
  description: string | null;
  status: string;
  syncStatus: string;
  sourceType: 'TEMPLATE_PUBLISH' | 'DESIGN_GENERATED' | string;
  previewImageUrl: string | null;
  externalProductId: string | null;
  publishMethod: string;
  requiresRepublish: boolean;
  configVersion: number;
  designId: string | null;
  upt: {
    id: string;
    name: string;
    slug: string;
    thumbnailUrl: string | null;
  } | null;
  /** Nullable: DESIGN_GENERATED rows may have no connection yet. */
  connection: {
    id: string;
    name: string;
    type: string;
    externalDomain: string | null;
  } | null;
  /** Bound design row (apparel multi-side v2). */
  design: {
    id: string;
    designRefId: string | null;
    previewUrl: string | null;
    sourceLibraryDesignId: string | null;
  } | null;
  renderSummary: RenderSummary;
  /**
   * Effective fulfillment settings (override ?? store defaults) resolved
   * server-side. Optional so older API responses keep parsing.
   */
  fulfillment?: {
    lane: FulfillmentLane;
    effective: EffectiveFulfillmentSettings;
    syncStatus: string;
  };
  /** Canonical print sides (one per PrintArea), ordered primary-first. */
  printSides: PrintSideMeta[];
  variants: ProductVariantRow[];
  createdAt: string;
  updatedAt: string;
}

interface RerenderResponse {
  productId: string;
  enqueued: { printExportJobIds: string[]; variantPreviewJobIds: string[] };
}

interface ConnectionSummary {
  id: string;
  organizationId: string;
  type: string;
  name: string;
  status: string;
  shopDomain: string | null;
}

interface PublishToShopifyResponse {
  data: {
    productId: string;
    externalProductId: string;
    variantCount: number;
    mode: 'CREATE' | 'DRIFT_UPDATE';
  };
}

interface UpdatePricesResponse {
  data: {
    productId: string;
    updated: number;
    shopifySynced: boolean;
    warning?: string;
  };
}

interface SyncFromShopifyResponse {
  productId: string;
  syncStatus: string;
  productStatus: string;
  mirroredFields: string[];
  removedVariantCount: number;
  configMetafieldRebuilt: boolean;
}

export function MyProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const api = useApiClient();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['portal', 'products', id],
    queryFn: () =>
      api.get<{ data?: MyProductDetail } & MyProductDetail>(
        `/api/v1/products/${id}`,
      ),
    enabled: !!id,
    // Adaptive refetch: 5 s while renders are in-flight, 60 s
    // otherwise. The variant-preview pipeline typically completes in
    // 5–15 s per side, so 5 s gives the merchant near-real-time
    // progress without thrashing the API.
    refetchInterval: (q) => {
      const raw = q.state.data as
        | { data?: MyProductDetail }
        | MyProductDetail
        | undefined;
      const p =
        (raw && 'data' in raw && raw.data) ||
        (raw as MyProductDetail | undefined);
      if (!p) return 60_000;
      const s = p.renderSummary;
      return s && s.pending + s.running > 0 ? 5_000 : 60_000;
    },
  });

  const raw = query.data as unknown as
    | ({ data?: MyProductDetail } & MyProductDetail)
    | undefined;
  const product: MyProductDetail | undefined = raw?.data ?? raw;

  const rerenderMutation = useMutation({
    mutationFn: () => api.post<RerenderResponse>(`/api/v1/products/${id}/re-render`, {}),
    onSuccess: () => {
      // Invalidate the detail query — the optimistic state reset on
      // the server side (variants → PENDING) is now reflected in DB,
      // so a fresh fetch shows the "queued" badges immediately.
      queryClient.invalidateQueries({ queryKey: ['portal', 'products', id] });
    },
  });

  // Full-sync model: "Sync from Shopify" re-fetches the authoritative product
  // state (title/status/prices/variants), marks removed variants and resolves
  // the SYNC_REQUIRED flag. Available for any published product outside an
  // in-flight publish.
  const syncFromShopifyMutation = useMutation({
    mutationFn: () =>
      api.post<SyncFromShopifyResponse>(
        `/api/v1/products/${id}/sync-from-shopify`,
        {},
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal', 'products', id] });
      queryClient.invalidateQueries({ queryKey: ['portal', 'products'] });
    },
  });

  const isDesignGenerated = product?.sourceType === 'DESIGN_GENERATED';
  const hasConnection = !!product?.connection;
  const canSyncFromShopify =
    !!product?.connection &&
    !!product?.externalProductId &&
    product.syncStatus !== 'PENDING' &&
    product.syncStatus !== 'SYNCING';
  const syncActionRequired =
    product?.syncStatus === 'SYNC_REQUIRED' ||
    product?.syncStatus === 'DRIFT_DETECTED';
  const renderInFlightForPublish =
    !!product &&
    product.renderSummary.pending + product.renderSummary.running > 0;
  const renderFailedForPublish =
    !!product && product.renderSummary.failed > 0;
  const canPublishNow =
    isDesignGenerated &&
    !!product &&
    !renderInFlightForPublish &&
    !renderFailedForPublish &&
    product.renderSummary.total > 0;

  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const idempotencyKeyRef = useRef<string | null>(null);

  const connectionsQuery = useQuery({
    queryKey: ['portal', 'connections'],
    queryFn: () =>
      api.get<ConnectionSummary[]>('/api/v1/organizations/current/connections'),
    enabled: publishDialogOpen,
  });

  const connections = connectionsQuery.data ?? [];
  const activeShopifyConnections = useMemo(
    () =>
      connections.filter(
        (c) => c.type === 'SHOPIFY' && c.status === 'ACTIVE',
      ),
    [connections],
  );

  const [selectedConnectionId, setSelectedConnectionId] = useState<string>('');
  useEffect(() => {
    if (
      publishDialogOpen &&
      !selectedConnectionId &&
      activeShopifyConnections.length > 0
    ) {
      // Pre-select if the product already has a connection (drift
      // re-publish case) or fall back to the only active connection.
      const initial =
        (hasConnection &&
          activeShopifyConnections.find(
            (c) => c.id === product?.connection?.id,
          )?.id) ||
        activeShopifyConnections[0].id;
      setSelectedConnectionId(initial);
    }
  }, [
    publishDialogOpen,
    selectedConnectionId,
    activeShopifyConnections,
    hasConnection,
    product?.connection?.id,
  ]);

  const publishMutation = useMutation({
    mutationFn: () => {
      if (!selectedConnectionId) {
        throw new Error('Please select a destination store.');
      }
      if (!idempotencyKeyRef.current) {
        idempotencyKeyRef.current = generateUuidV4();
      }
      // Attach the reviewed selling prices (pricing step) so the first
      // publish / re-publish sets exactly what the merchant confirmed.
      const variantPrices = collectVariantPrices() ?? undefined;
      return api.post<PublishToShopifyResponse>(
        `/api/v1/products/${id}/publish-to-shopify`,
        { connectionId: selectedConnectionId, variantPrices },
        {
          headers: {
            'Idempotency-Key': idempotencyKeyRef.current,
          },
        },
      );
    },
    onSuccess: () => {
      setPublishDialogOpen(false);
      setPublishError(null);
      idempotencyKeyRef.current = null;
      queryClient.invalidateQueries({ queryKey: ['portal', 'products', id] });
      queryClient.invalidateQueries({ queryKey: ['portal', 'products'] });
    },
    onError: (err: unknown) => {
      const message =
        err instanceof Error ? err.message : 'Failed to publish.';
      setPublishError(message);
    },
  });

  const closePublishDialog = () => {
    if (publishMutation.isPending) return;
    setPublishDialogOpen(false);
    setPublishError(null);
    idempotencyKeyRef.current = null;
  };

  const openPublishDialog = () => {
    idempotencyKeyRef.current = generateUuidV4();
    setPublishError(null);
    setPublishDialogOpen(true);
  };

  // Edit Design launch: mint a short-lived launch token with
  // purpose=edit-design, then navigate to the designer SPA with the
  // token in the URL. The designer trades it for a designer-session
  // cookie on boot and (per `purpose`) loads the bound design's
  // designJson via GET /designer/products/:id/edit-design.
  const editDesignMutation = useMutation({
    mutationFn: async (p: MyProductDetail) => {
      if (!p.designId || !p.upt?.id) {
        throw new Error('Product is missing design or template metadata.');
      }
      const res = await api.post<{
        data: {
          token: string;
          designerUrl: string;
          expiresAt: number;
        };
      }>('/api/v1/auth/designer-launch-token', {
        uptId: p.upt.id,
        returnUrl: `${window.location.origin}/products/${p.id}`,
        designId: p.designId,
        productId: p.id,
        purpose: 'edit-design',
      });
      return res.data;
    },
    onSuccess: (res, variables) => {
      const url = new URL(`${res.designerUrl}/designer`);
      url.searchParams.set('context', 'edit-product');
      url.searchParams.set('token', res.token);
      // Mirror the create-product flow: include uptId + productId on
      // the URL so the designer SPA can boot the canvas without
      // waiting for the session payload to round-trip. The API will
      // still validate every id against the session cookie + JWT
      // claims server-side; URL params are inputs, not credentials.
      if (variables.upt?.id) {
        url.searchParams.set('uptId', variables.upt.id);
      }
      url.searchParams.set('productId', variables.id);
      url.searchParams.set(
        'returnUrl',
        `${window.location.origin}/products/${variables.id}`,
      );
      window.location.href = url.toString();
    },
  });

  const launchEditDesign = (p: MyProductDetail) => {
    editDesignMutation.mutate(p);
  };

  // ── Pricing editor state (enterprise bulk pricing UX) ────────────────────
  // Draft selling prices keyed by variant id. Synced from the loaded product
  // whenever the underlying price signature changes, so background polling
  // never clobbers an in-progress edit.
  const [priceDrafts, setPriceDrafts] = useState<Record<string, string>>({});
  // Single-dropdown bulk control model: operation + unit + value.
  const [bulkOp, setBulkOp] = useState<PriceBulkOp>('set');
  const [bulkUnit, setBulkUnit] = useState<PriceBulkUnit>('amount');
  const [bulkValue, setBulkValue] = useState('');
  const [pricingError, setPricingError] = useState<string | null>(null);

  // Full-sync model: variants removed on Shopify stay in the payload (marked
  // `externalDeletedAt`) but are excluded from pricing and shown separately.
  const activeVariants = useMemo(
    () => (product ? product.variants.filter((v) => !v.externalDeletedAt) : []),
    [product],
  );
  const removedVariants = useMemo(
    () => (product ? product.variants.filter((v) => v.externalDeletedAt) : []),
    [product],
  );

  // ── Grouped / filterable variants (large option matrices) ────────────────
  // Option axes derived from ProductVariant.options (e.g. Color, Size). Only
  // axes with 2+ distinct values become filters — a single-value axis adds
  // noise without narrowing anything.
  const optionAxes = useMemo(() => {
    const axes = new Map<string, string[]>();
    for (const v of activeVariants) {
      const opts = v.options;
      if (!opts || typeof opts !== 'object' || Array.isArray(opts)) continue;
      for (const [key, val] of Object.entries(opts as Record<string, unknown>)) {
        if (typeof val !== 'string' || val.length === 0) continue;
        const list = axes.get(key) ?? [];
        if (!list.includes(val)) list.push(val);
        axes.set(key, list);
      }
    }
    return [...axes.entries()].filter(([, values]) => values.length > 1);
  }, [activeVariants]);

  // Active option filters (axis → selected value; '' = all).
  const [optionFilters, setOptionFilters] = useState<Record<string, string>>(
    {},
  );
  const [templateBulkGroup, setTemplateBulkGroup] = useState<{
    sizeLabel: string;
    items: ProductVariantRow[];
  } | null>(null);

  const visibleVariants = useMemo(() => {
    const entries = Object.entries(optionFilters).filter(([, v]) => v);
    if (entries.length === 0) return activeVariants;
    return activeVariants.filter((v) => {
      const opts = (v.options ?? {}) as Record<string, unknown>;
      return entries.every(([key, val]) => opts[key] === val);
    });
  }, [activeVariants, optionFilters]);

  const variantPriceSignature = useMemo(() => {
    return activeVariants
      .map((v) => `${v.id}:${formatMoneyValue(Number.parseFloat(v.price))}`)
      .join('|');
  }, [activeVariants]);

  useEffect(() => {
    if (!product) return;
    const next: Record<string, string> = {};
    for (const v of product.variants) {
      if (v.externalDeletedAt) continue;
      next[v.id] = formatMoneyValue(Number.parseFloat(v.price));
    }
    setPriceDrafts(next);
    // Intentionally keyed on the price signature only: identical refetches
    // (background polling) won't clobber in-progress edits, while a real
    // price change (e.g. after saving) reseeds the drafts. The `product`
    // values read here are fully captured by `variantPriceSignature`.
  }, [variantPriceSignature]);

  const handlePriceDraftChange = (variantId: string, value: string) => {
    setPriceDrafts((prev) => ({ ...prev, [variantId]: value }));
  };

  // Apply the configured bulk operation to every VISIBLE variant (respects
  // the active option filters, so "increase Size M by $2" is one click:
  // filter Size=M, apply). `margin` is the only op that can skip a variant
  // (no cost) — all others are always applicable.
  const applyBulkToAll = () => {
    if (!product) return;
    // Value magnitude only — direction comes from `bulkOp`, so a non-negative
    // parse (which also strips stray $/%) is exactly right here.
    const value = parseMoneyInput(bulkValue);
    if (value == null) {
      setPricingError('Enter a value for the bulk pricing operation.');
      return;
    }
    setPricingError(null);
    setPriceDrafts((prev) => {
      const next = { ...prev };
      for (const v of visibleVariants) {
        const base = parseMoneyInput(prev[v.id] ?? v.price) ?? 0;
        const cost = v.cost != null ? Number.parseFloat(v.cost) : null;
        const result = adjustPrice(base, bulkOp, bulkUnit, value, cost);
        if (result == null) continue;
        next[v.id] = formatMoneyValue(result);
      }
      return next;
    });
  };

  const collectVariantPrices = ():
    | Array<{ variantId: string; price: number }>
    | null => {
    if (!product) return null;
    const out: Array<{ variantId: string; price: number }> = [];
    for (const v of activeVariants) {
      const parsed = parseMoneyInput(priceDrafts[v.id] ?? v.price);
      if (parsed == null || parsed <= 0) {
        setPricingError(
          `Enter a valid selling price for "${v.title ?? v.internalSku}".`,
        );
        return null;
      }
      out.push({ variantId: v.id, price: parsed });
    }
    return out;
  };

  const hasPriceChanges = useMemo(() => {
    return activeVariants.some(
      (v) =>
        (priceDrafts[v.id] ?? '') !==
        formatMoneyValue(Number.parseFloat(v.price)),
    );
  }, [activeVariants, priceDrafts]);

  // Non-blocking "below cost" warning: how many variants currently price under
  // their wholesale cost (negative profit). Store owners may intentionally sell
  // at a loss, so this only warns — it never blocks save/publish.
  const belowCostCount = useMemo(() => {
    return activeVariants.reduce((acc, v) => {
      const price = parseMoneyInput(priceDrafts[v.id] ?? v.price);
      const cost = v.cost != null ? Number.parseFloat(v.cost) : null;
      return price != null && isBelowCost(price, cost) ? acc + 1 : acc;
    }, 0);
  }, [activeVariants, priceDrafts]);

  const savePricesMutation = useMutation({
    mutationFn: () => {
      const variants = collectVariantPrices();
      if (!variants) throw new Error('Invalid prices');
      return api.patch<UpdatePricesResponse>(
        `/api/v1/products/${id}/prices`,
        { variants },
      );
    },
    onSuccess: (res) => {
      setPricingError(null);
      const warning = res?.data?.warning;
      if (warning) setPricingError(warning);
      queryClient.invalidateQueries({ queryKey: ['portal', 'products', id] });
    },
    onError: (err: unknown) => {
      setPricingError(
        err instanceof Error ? err.message : 'Failed to save prices.',
      );
    },
  });

  // Shared pricing controls (bulk toolbar + save action + warnings) rendered
  // inside whichever variant table card is active. Kept as a local renderer so
  // both the DESIGN_GENERATED and TEMPLATE_PUBLISH cards stay in sync.
  const renderPricingControls = () => (
    <div className="space-y-2 px-4 pt-3 sm:px-5">
      {optionAxes.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {optionAxes.map(([axis, values]) => (
            <label
              key={axis}
              className="flex items-center gap-1.5 text-xs text-text-secondary"
            >
              {axis}
              <select
                aria-label={`Filter variants by ${axis}`}
                value={optionFilters[axis] ?? ''}
                onChange={(e) =>
                  setOptionFilters((prev) => ({
                    ...prev,
                    [axis]: e.target.value,
                  }))
                }
                className="rounded-md border border-border bg-surface px-2 py-1 text-sm text-text-primary focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
              >
                <option value="">All {axis.toLowerCase()}s</option>
                {values.map((val) => (
                  <option key={val} value={val}>
                    {val}
                  </option>
                ))}
              </select>
            </label>
          ))}
          <span className="text-[11px] text-text-tertiary">
            Showing {visibleVariants.length} of {activeVariants.length}{' '}
            variants
            {Object.values(optionFilters).some(Boolean) &&
              ' — bulk pricing applies to shown variants only'}
          </span>
          {Object.values(optionFilters).some(Boolean) && (
            <button
              type="button"
              className="text-[11px] font-medium text-brand-600 hover:underline dark:text-brand-400"
              onClick={() => setOptionFilters({})}
            >
              Clear filters
            </button>
          )}
        </div>
      )}
      <BulkPricingToolbar
        op={bulkOp}
        setOp={setBulkOp}
        unit={bulkUnit}
        setUnit={setBulkUnit}
        value={bulkValue}
        setValue={setBulkValue}
        onApply={applyBulkToAll}
        trailing={
          hasPriceChanges ? (
            <Button
              variant="primary"
              size="sm"
              onClick={() => savePricesMutation.mutate()}
              disabled={savePricesMutation.isPending}
            >
              {savePricesMutation.isPending ? 'Saving…' : 'Save prices'}
            </Button>
          ) : null
        }
      />
      {belowCostCount > 0 && (
        <p className="text-xs text-warning">
          {belowCostCount === 1
            ? '1 variant is priced below its cost (selling at a loss).'
            : `${belowCostCount} variants are priced below their cost (selling at a loss).`}
        </p>
      )}
      {pricingError && <p className="text-xs text-danger">{pricingError}</p>}
    </div>
  );

  const shopifyAdminUrl = useMemo(() => {
    if (!product?.connection) return null;
    if (
      product.connection.type !== 'SHOPIFY' ||
      !product.connection.externalDomain ||
      !product.externalProductId
    ) {
      return null;
    }
    return `https://${product.connection.externalDomain}/admin/products/${product.externalProductId.replace(
      'gid://shopify/Product/',
      '',
    )}`;
  }, [product?.connection, product?.externalProductId]);

  if (query.isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="lg" />
      </div>
    );
  }
  if (query.isError) {
    return (
      <div className="py-24 text-center">
        <p className="text-sm text-danger mb-3">Failed to load product.</p>
        <Button variant="outline" size="sm" onClick={() => query.refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  if (!product?.id) {
    return (
      <div className="py-24 text-center text-sm text-text-tertiary">
        Product not found.
      </div>
    );
  }

  const summary = product.renderSummary;
  const renderInFlight = summary.pending + summary.running > 0;
  const renderFailed = !renderInFlight && summary.failed > 0;
  // Re-render is available for any DESIGN_GENERATED product with at
  // least one variant — covers both "failed and stuck" and "I just
  // want to try again" merchant scenarios.
  const canRerender = isDesignGenerated && product.variants.length > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-border bg-surface px-4 py-3 sm:px-5">
        <div className="flex items-center gap-4 min-w-0">
          <button
            type="button"
            onClick={() => navigate('/products')}
            className="flex items-center justify-center rounded-md border border-border bg-surface p-2 text-text-secondary transition-colors hover:bg-surface-secondary hover:text-text-primary"
            aria-label="Back to My Products"
            title="Back to My Products"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-lg font-bold text-text-primary sm:text-xl">
                {product.title}
              </h1>
              {isDesignGenerated && (
                <Badge variant="info" size="sm">
                  Design
                </Badge>
              )}
              <Badge
                variant={STATUS_VARIANT[product.status] ?? 'default'}
                size="sm"
              >
                {product.status.replace(/_/g, ' ')}
              </Badge>
              <Badge
                variant={SYNC_VARIANT[product.syncStatus] ?? 'default'}
                size="sm"
              >
                {SYNC_LABEL[product.syncStatus] ??
                  product.syncStatus.replace(/_/g, ' ')}
              </Badge>
              {product.requiresRepublish && (
                <Badge
                  variant={
                    product.renderSummary.pending + product.renderSummary.running > 0
                      ? "info"
                      : "warning"
                  }
                  size="sm"
                >
                  {product.renderSummary.pending + product.renderSummary.running > 0
                    ? "Updating images"
                    : "Republish required"}
                </Badge>
              )}
              {product.fulfillment?.effective.provider === 'SELF' && (
                <Badge variant="warning" size="sm">
                  Self-fulfilled
                </Badge>
              )}
              {product.fulfillment?.effective.provider === 'TK_POD' &&
                product.fulfillment.effective.method === 'MANUAL' && (
                  <Badge variant="info" size="sm">
                    Manual fulfillment
                  </Badge>
                )}
              {product.fulfillment?.syncStatus === 'ERROR' && (
                <Badge variant="danger" size="sm">
                  Fulfillment sync error
                </Badge>
              )}
              {product.fulfillment && product.connection && (
                <Link
                  to={`/settings/fulfillment?connectionId=${product.connection.id}`}
                  className="text-xs font-medium text-brand-600 hover:underline dark:text-brand-400"
                >
                  Fulfillment settings
                </Link>
              )}
            </div>
            <p className="mt-0.5 truncate text-[11px] text-text-tertiary">
              {product.connection
                ? `${product.connection.externalDomain ?? product.connection.name} · Published ${new Date(product.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                : `Not published yet · Created ${new Date(product.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isDesignGenerated && product.designId && product.upt?.id && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => launchEditDesign(product)}
              disabled={
                editDesignMutation.isPending ||
                product.renderSummary.pending + product.renderSummary.running > 0
              }
              title={
                editDesignMutation.isPending
                  ? 'Opening designer…'
                  : product.renderSummary.pending + product.renderSummary.running > 0
                    ? 'Images are still generating. Edit again when every preview has finished.'
                    : 'Reopen this product in the designer to update the artwork.'
              }
            >
              {editDesignMutation.isPending
                ? 'Opening…'
                : product.renderSummary.pending + product.renderSummary.running > 0
                  ? 'Generating images…'
                  : 'Edit Design'}
            </Button>
          )}
          {isDesignGenerated && !hasConnection && (
            <Button
              variant="primary"
              size="sm"
              onClick={openPublishDialog}
              disabled={!canPublishNow}
              title={
                renderInFlightForPublish
                  ? 'Wait for all variant previews to finish rendering before publishing.'
                  : renderFailedForPublish
                    ? 'Resolve render failures before publishing.'
                    : product.renderSummary.total === 0
                      ? 'This product has no variants to publish.'
                      : undefined
              }
            >
              Publish to Shopify
            </Button>
          )}
          {isDesignGenerated &&
            hasConnection &&
            product.requiresRepublish && (
              <Button
                variant="primary"
                size="sm"
                onClick={openPublishDialog}
                disabled={!canPublishNow}
                title={
                  renderInFlightForPublish
                    ? 'Wait for all variant previews to finish rendering before re-publishing.'
                    : renderFailedForPublish
                      ? 'Resolve render failures before re-publishing.'
                      : undefined
                }
              >
                Re-publish to Shopify
              </Button>
            )}
          {canSyncFromShopify && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => syncFromShopifyMutation.mutate()}
              disabled={syncFromShopifyMutation.isPending}
              title="Re-fetch this product from Shopify and mirror any changes (title, status, prices, variants)."
            >
              {syncFromShopifyMutation.isPending
                ? 'Syncing…'
                : 'Sync from Shopify'}
            </Button>
          )}
          {shopifyAdminUrl && (
            <a href={shopifyAdminUrl} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm">
                Open in Shopify admin
              </Button>
            </a>
          )}
        </div>
      </div>

      {/* Sync required banner — full-sync model. Set when Shopify-side
          changes need merchant review (e.g. variants removed on the store).
          Also shown for legacy DRIFT_DETECTED rows; both resolve via the
          same "Sync from Shopify" action. */}
      {syncActionRequired && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-warning/30 bg-warning/5 px-4 py-3 text-sm">
          <div className="flex items-center gap-3 min-w-0">
            <span
              aria-hidden
              className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-warning/15 text-warning"
            >
              !
            </span>
            <div className="min-w-0">
              <p className="font-medium">
                This product changed on Shopify and needs a sync
              </p>
              <p className="text-[11px] text-text-tertiary mt-0.5">
                {removedVariants.length > 0
                  ? `${removedVariants.length} variant${removedVariants.length === 1 ? ' was' : 's were'} removed on the store. Removed variants are blocked from new orders. Sync to confirm the current state.`
                  : 'Sync to pull the latest product state (title, status, prices, variants) from Shopify.'}
              </p>
            </div>
          </div>
          {canSyncFromShopify && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => syncFromShopifyMutation.mutate()}
              disabled={syncFromShopifyMutation.isPending}
            >
              {syncFromShopifyMutation.isPending
                ? 'Syncing…'
                : 'Sync from Shopify'}
            </Button>
          )}
        </div>
      )}
      {syncFromShopifyMutation.isError && (
        <div className="rounded-lg border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger">
          Sync from Shopify failed. Please try again — if the problem
          persists, check the store connection.
        </div>
      )}

      {/* Drift banner — DESIGN_GENERATED only, when bound design has
          diverged from the published variant images. */}
      {isDesignGenerated && hasConnection && product.requiresRepublish && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-warning/30 bg-warning/5 px-4 py-3 text-sm">
          <div className="flex items-center gap-3 min-w-0">
            <span
              aria-hidden
              className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-warning/15 text-warning"
            >
              !
            </span>
            <div className="min-w-0">
              <p className="font-medium">
                Design changes are not yet on Shopify
              </p>
              <p className="text-[11px] text-text-tertiary mt-0.5">
                The bound design was edited after the last publish.
                Re-publish to push the latest variant previews to
                {product.connection?.externalDomain
                  ? ` ${product.connection.externalDomain}`
                  : ' your store'}
                .
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={openPublishDialog}
            disabled={!canPublishNow}
          >
            Re-publish to Shopify
          </Button>
        </div>
      )}

      {/* Render status banner — DESIGN_GENERATED only */}
      {isDesignGenerated && (renderInFlight || renderFailed) && (
        <div
          className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3 text-sm ${
            renderInFlight
              ? 'border-info/30 bg-info/5 text-text-primary'
              : 'border-danger/30 bg-danger/5 text-text-primary'
          }`}
        >
          <div className="flex items-center gap-3 min-w-0">
            {renderInFlight ? (
              <Spinner size="sm" />
            ) : (
              <span
                aria-hidden
                className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-danger/15 text-danger"
              >
                !
              </span>
            )}
            <div className="min-w-0">
              <p className="font-medium">
                {renderInFlight
                  ? `Rendering variants — ${summary.done} of ${summary.total} done`
                  : `Render failed on ${summary.failed} of ${summary.total} variants`}
              </p>
              <p className="text-[11px] text-text-tertiary mt-0.5">
                {renderInFlight
                  ? 'Previews will appear automatically as each variant finishes.'
                  : 'Re-running the pipeline usually clears transient failures.'}
              </p>
            </div>
          </div>
          {canRerender && renderFailed && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => rerenderMutation.mutate()}
              disabled={rerenderMutation.isPending}
            >
              {rerenderMutation.isPending ? 'Re-queuing…' : 'Re-render preview'}
            </Button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
        {/* Left — overview + variants */}
        <div className="space-y-4">
          <Card>
            <CardHeader title="Overview" />
            <div className="space-y-3 px-4 pb-4 sm:px-5 sm:pb-5">
              <div className="flex items-center gap-3">
                {/* Composite preview prefers Product.previewImageUrl
                    (variant-preview worker's atomic claim), falling
                    back to UPT thumbnail for catalog products. */}
                {product.previewImageUrl ? (
                  <img
                    src={product.previewImageUrl}
                    alt=""
                    className="h-14 w-14 shrink-0 rounded border border-border object-cover bg-surface-secondary"
                  />
                ) : product.upt?.thumbnailUrl ? (
                  <img
                    src={product.upt.thumbnailUrl}
                    alt=""
                    className="h-14 w-14 shrink-0 rounded border border-border object-cover bg-surface-secondary"
                  />
                ) : (
                  <div className="h-14 w-14 shrink-0 rounded border border-border bg-surface-secondary" />
                )}
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
                    {isDesignGenerated ? 'Design' : 'Template'}
                  </p>
                  <p className="truncate text-sm font-medium text-text-primary">
                    {product.upt?.name ?? '—'}
                  </p>
                  {product.design?.designRefId && (
                    <p className="font-mono text-[11px] text-text-tertiary truncate">
                      {product.design.designRefId}
                    </p>
                  )}
                </div>
              </div>
              {product.description && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary mb-1">
                    Description
                  </p>
                  <p className="text-sm text-text-primary whitespace-pre-wrap">
                    {product.description}
                  </p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary mb-1">
                    External Product ID
                  </p>
                  <p className="font-mono text-xs break-all text-text-primary">
                    {product.externalProductId ?? '—'}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary mb-1">
                    Publish method
                  </p>
                  <p className="text-xs text-text-primary">
                    {product.publishMethod.replace(/_/g, ' ').toLowerCase()}
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Variant TABLE for DESIGN_GENERATED products. Front + Back,
              when both have artwork, are the Composite preview column.
              Extra sides are on the Preview gallery tabs, not extra tables. */}
          {isDesignGenerated ? (
            <>
              <ProductPreviewGallery
                productTitle={product.title}
                printSides={product.printSides}
                variants={product.variants}
              />
              <Card padding="none">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3 sm:px-5">
                <h3 className="text-sm font-semibold text-text-primary">
                  Variants ({activeVariants.length}
                  {removedVariants.length > 0 &&
                    ` · ${removedVariants.length} removed`}
                  )
                </h3>
                {canRerender && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => rerenderMutation.mutate()}
                    disabled={rerenderMutation.isPending}
                    title="Re-enqueue the render pipeline for every variant"
                  >
                    {rerenderMutation.isPending
                      ? 'Re-queuing…'
                      : 'Re-render preview'}
                  </Button>
                )}
              </div>
              {product.variants.length === 0 ? (
                <div className="px-4 pb-4 sm:px-5 sm:pb-5">
                  <p className="text-xs text-text-tertiary">
                    No variants — this product has not been bound to any
                    blank variants yet.
                  </p>
                </div>
              ) : (
                <>
                  {renderPricingControls()}
                  <DesignGeneratedVariantsTable
                    variants={visibleVariants}
                    drafts={priceDrafts}
                    onPriceChange={handlePriceDraftChange}
                  />
                </>
              )}
            </Card>
            </>
          ) : (
            // TEMPLATE_PUBLISH products never run through the apparel render
            // pipeline, but selling price is still editable here (same merged
            // pricing model as design-generated products).
            <Card padding="none">
              <CardHeader
                title={`Variants (${activeVariants.length}${removedVariants.length > 0 ? ` · ${removedVariants.length} removed` : ''})`}
              />
              {product.variants.length === 0 ? (
                <div className="px-4 pb-4 sm:px-5 sm:pb-5">
                  <p className="text-xs text-text-tertiary">
                    No variants published for this product yet.
                  </p>
                </div>
              ) : (
                <>
                  {renderPricingControls()}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border bg-surface-secondary/40">
                          <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
                            Variant
                          </th>
                          <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
                            SKU
                          </th>
                          <th
                            className="px-5 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-text-tertiary"
                            title="Wholesale cost Transfer Kingdom will invoice per unit"
                          >
                            Cost
                          </th>
                          <th className="px-5 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
                            Your selling price
                          </th>
                          <th className="px-5 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
                            Profit
                          </th>
                        </tr>
                      </thead>
                      <GroupedVariantTable
                        variants={visibleVariants}
                        getOptions={(v) => v.options}
                        colSpan={5}
                        renderGroupActions={(group) => (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              setTemplateBulkGroup({
                                sizeLabel: group.sizeLabel,
                                items: group.items,
                              })
                            }
                          >
                            Edit bulk
                          </Button>
                        )}
                        renderRow={(v) => (
                          <tr key={v.id}>
                            <td className="px-5 py-2.5 text-text-primary">
                              {v.title ?? '—'}
                            </td>
                            <td className="px-5 py-2.5 font-mono text-xs text-text-secondary">
                              {v.externalSku ?? v.internalSku}
                            </td>
                            <td className="px-5 py-2.5 text-right text-text-secondary">
                              {v.cost
                                ? `$${Number.parseFloat(v.cost).toFixed(2)}`
                                : '—'}
                            </td>
                            <td className="px-5 py-2.5 text-right">
                              <EditablePriceInput
                                value={priceDrafts[v.id] ?? v.price}
                                onChange={(val) =>
                                  handlePriceDraftChange(v.id, val)
                                }
                                ariaLabel={`Selling price for ${v.title ?? v.internalSku}`}
                              />
                            </td>
                            <ProfitCell
                              price={priceDrafts[v.id] ?? v.price}
                              cost={v.cost}
                            />
                          </tr>
                        )}
                      />
                      {Object.values(optionFilters).every((f) => !f) &&
                      removedVariants.length > 0 ? (
                        <tbody className="divide-y divide-border">
                          {removedVariants.map((v) => (
                            <tr key={v.id} className="opacity-50">
                              <td className="px-5 py-2.5 text-text-primary">
                                <span className="flex items-center gap-2">
                                  <span className="line-through">
                                    {v.title ?? '—'}
                                  </span>
                                  <Badge variant="danger" size="sm">
                                    Removed on Shopify
                                  </Badge>
                                </span>
                              </td>
                              <td className="px-5 py-2.5 font-mono text-xs text-text-secondary">
                                {v.externalSku ?? v.internalSku}
                              </td>
                              <td className="px-5 py-2.5 text-right text-text-secondary">
                                {v.cost
                                  ? `$${Number.parseFloat(v.cost).toFixed(2)}`
                                  : '—'}
                              </td>
                              <td className="px-5 py-2.5 text-right text-text-secondary">
                                ${Number.parseFloat(v.price).toFixed(2)}
                              </td>
                              <td className="px-5 py-2.5 text-right text-text-tertiary">
                                —
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      ) : null}
                    </table>
                  </div>
                  <VariantBulkPriceModal
                    open={templateBulkGroup != null}
                    onClose={() => setTemplateBulkGroup(null)}
                    sizeLabel={templateBulkGroup?.sizeLabel ?? ''}
                    items={templateBulkGroup?.items ?? []}
                    drafts={priceDrafts}
                    onApply={(next) => {
                      for (const [id, val] of Object.entries(next)) {
                        handlePriceDraftChange(id, val);
                      }
                    }}
                  />
                </>
              )}
            </Card>
          )}

          {/* Print files — canonical per-side 300 DPI print PNGs (one per
              PrintArea, productVariantId NULL). The same file is reused across
              every variant of the product. Only present for design-bound
              products; TEMPLATE_PUBLISH rows have no design parts. */}
          {product.printSides.length > 0 && (
            <Card padding="none">
              <CardHeader title={`Print files (${product.printSides.length})`} />
              <div className="px-4 pb-4 sm:px-5 sm:pb-5">
                <p className="mb-3 text-xs text-text-tertiary">
                  300 DPI transparent print PNG per side — the same file used for
                  production, shared across every variant of this product.
                </p>
                <ul className="space-y-1.5">
                  {product.printSides.map((side) => (
                    <li
                      key={side.printAreaId}
                      className="flex items-center justify-between gap-2 rounded border border-border bg-surface px-2.5 py-1.5"
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        {side.exportUrl ? (
                          <a
                            href={side.exportUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="shrink-0"
                            title={`Open ${side.displayName} print file`}
                          >
                            <img
                              src={side.exportUrl}
                              alt={`${side.displayName} print file`}
                              className="h-9 w-9 rounded border border-border bg-surface-secondary object-contain transition-opacity hover:opacity-80"
                            />
                          </a>
                        ) : (
                          <div className="h-9 w-9 shrink-0 rounded border border-border bg-surface-secondary" />
                        )}
                        <div className="min-w-0">
                          <p className="flex items-center gap-1.5 truncate text-xs font-medium text-text-primary">
                            {side.displayName}
                            {side.isPrimary && (
                              <Badge variant="info" size="sm">
                                Primary
                              </Badge>
                            )}
                          </p>
                        </div>
                      </div>
                      {side.exportStatus === 'READY' && side.exportUrl ? (
                        <a
                          href={side.exportUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-text-primary px-2.5 py-1.5 text-[11px] font-medium text-surface transition-opacity hover:opacity-90"
                        >
                          <svg
                            className="h-3.5 w-3.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                            />
                          </svg>
                          Download PNG
                        </a>
                      ) : side.exportStatus === 'FAILED' ? (
                        <Badge variant="danger" size="sm">
                          Failed
                        </Badge>
                      ) : (
                        <span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-text-secondary">
                          <Spinner size="sm" />
                          Processing…
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </Card>
          )}
        </div>

        {/* Right — store + meta */}
        <div className="space-y-4">
          <Card>
            <CardHeader title="Store" />
            <div className="space-y-1 px-4 pb-4 sm:px-5 sm:pb-5 text-sm">
              {product.connection ? (
                <>
                  <p className="text-text-primary font-medium">
                    {product.connection.name}
                  </p>
                  {product.connection.externalDomain && (
                    <p className="text-xs text-text-secondary break-all">
                      {product.connection.externalDomain}
                    </p>
                  )}
                  <p className="text-[11px] text-text-tertiary mt-1">
                    Source: {product.connection.type}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-text-primary font-medium">Not published</p>
                  <p className="text-xs text-text-secondary">
                    This product hasn&apos;t been pushed to a store yet.
                  </p>
                </>
              )}
            </div>
          </Card>

          <Card>
            <CardHeader title="Last updated" />
            <div className="px-4 pb-4 sm:px-5 sm:pb-5 text-sm">
              <p className="text-text-primary">
                {new Date(product.updatedAt).toLocaleString()}
              </p>
              <p className="mt-2 text-[11px] text-text-tertiary">
                Configuration version {product.configVersion}
              </p>
            </div>
          </Card>

          {rerenderMutation.isError && (
            <Card>
              <CardHeader title="Re-render failed" />
              <div className="px-4 pb-4 sm:px-5 sm:pb-5 text-sm">
                <p className="text-danger text-xs">
                  {(rerenderMutation.error as Error)?.message ?? 'Unknown error'}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => rerenderMutation.reset()}
                >
                  Dismiss
                </Button>
              </div>
            </Card>
          )}

          {editDesignMutation.isError && (
            <Card>
              <CardHeader title="Could not open designer" />
              <div className="px-4 pb-4 sm:px-5 sm:pb-5 text-sm">
                <p className="text-danger text-xs">
                  {(editDesignMutation.error as Error)?.message ??
                    'Unknown error'}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => editDesignMutation.reset()}
                >
                  Dismiss
                </Button>
              </div>
            </Card>
          )}
        </div>
      </div>

      {publishDialogOpen && (
        <PublishToShopifyDialog
          mode={hasConnection ? 'REPUBLISH' : 'PUBLISH'}
          isLoading={connectionsQuery.isLoading}
          connections={activeShopifyConnections}
          selectedConnectionId={selectedConnectionId}
          onSelectConnection={setSelectedConnectionId}
          onClose={closePublishDialog}
          onConfirm={() => publishMutation.mutate()}
          isSubmitting={publishMutation.isPending}
          errorMessage={publishError}
          currentConnectionId={product.connection?.id ?? null}
          variants={activeVariants}
          priceDrafts={priceDrafts}
          onPriceChange={handlePriceDraftChange}
          bulkOp={bulkOp}
          setBulkOp={setBulkOp}
          bulkUnit={bulkUnit}
          setBulkUnit={setBulkUnit}
          bulkValue={bulkValue}
          setBulkValue={setBulkValue}
          onApplyBulk={applyBulkToAll}
          belowCostCount={belowCostCount}
        />
      )}
    </div>
  );
}

interface PublishDialogProps {
  mode: 'PUBLISH' | 'REPUBLISH';
  isLoading: boolean;
  connections: ConnectionSummary[];
  selectedConnectionId: string;
  onSelectConnection: (id: string) => void;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
  errorMessage: string | null;
  currentConnectionId: string | null;
  // Pricing step
  variants: ProductVariantRow[];
  priceDrafts: Record<string, string>;
  onPriceChange: (variantId: string, value: string) => void;
  bulkOp: PriceBulkOp;
  setBulkOp: (v: PriceBulkOp) => void;
  bulkUnit: PriceBulkUnit;
  setBulkUnit: (v: PriceBulkUnit) => void;
  bulkValue: string;
  setBulkValue: (v: string) => void;
  onApplyBulk: () => void;
  belowCostCount: number;
}

function PublishToShopifyDialog({
  mode,
  isLoading,
  connections,
  selectedConnectionId,
  onSelectConnection,
  onClose,
  onConfirm,
  isSubmitting,
  errorMessage,
  currentConnectionId,
  variants,
  priceDrafts,
  onPriceChange,
  bulkOp,
  setBulkOp,
  bulkUnit,
  setBulkUnit,
  bulkValue,
  setBulkValue,
  onApplyBulk,
  belowCostCount,
}: PublishDialogProps) {
  const isRepublish = mode === 'REPUBLISH';
  const title = isRepublish ? 'Re-publish to Shopify' : 'Publish to Shopify';
  const description = isRepublish
    ? 'Re-publishing replaces variant images with the latest renders and updates metafields. Existing variants and orders are preserved.'
    : 'Choose a Shopify store to publish this design. Variants, images, and metafields will be created on the destination store.';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={() => {
        if (!isSubmitting) onClose();
      }}
    >
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg border border-border bg-surface p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3">
          <h2 className="text-lg font-semibold text-text-primary">{title}</h2>
          <p className="mt-1 text-xs text-text-tertiary">{description}</p>
          {variants.length > 0 ? (
            <p className="mt-1 text-xs text-text-tertiary">
              {variants.length} variants, {countOptionAxes(variants)} options.
              Shopify Admin and this app support up to 2048 variants; the
              Online Store theme lists at most 250.
            </p>
          ) : null}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner size="sm" />
          </div>
        ) : connections.length === 0 ? (
          <div className="rounded border border-warning/30 bg-warning/5 p-3 text-xs text-text-secondary">
            <p className="font-medium text-text-primary">
              No active Shopify stores
            </p>
            <p className="mt-1">
              Connect a Shopify store from Settings → Stores before
              publishing.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
              Destination store
            </label>
            <div className="space-y-1.5">
              {connections.map((c) => {
                const locked =
                  isRepublish &&
                  currentConnectionId !== null &&
                  c.id !== currentConnectionId;
                const isSelected = c.id === selectedConnectionId;
                return (
                  <label
                    key={c.id}
                    className={`flex items-start gap-2 rounded border p-2.5 text-sm ${
                      locked
                        ? 'cursor-not-allowed border-border bg-surface-secondary/40 opacity-60'
                        : isSelected
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/40'
                    }`}
                  >
                    <input
                      type="radio"
                      name="publish-connection"
                      value={c.id}
                      checked={isSelected}
                      disabled={locked || isSubmitting}
                      onChange={() => onSelectConnection(c.id)}
                      className="mt-0.5"
                    />
                    <div className="min-w-0">
                      <p className="truncate font-medium text-text-primary">
                        {c.name}
                      </p>
                      {c.shopDomain && (
                        <p className="truncate text-[11px] text-text-tertiary">
                          {c.shopDomain}
                        </p>
                      )}
                      {locked && (
                        <p className="mt-1 text-[11px] text-warning">
                          Re-publish must target the original store.
                        </p>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {/* Pricing step — review/adjust selling prices before publishing.
            Only shown once a destination store is available. */}
        {!isLoading && connections.length > 0 && variants.length > 0 && (
          <div className="mt-4 border-t border-border pt-4">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
              Selling prices
            </p>
            <PricingEditor
              variants={variants}
              drafts={priceDrafts}
              onChange={onPriceChange}
              bulkOp={bulkOp}
              setBulkOp={setBulkOp}
              bulkUnit={bulkUnit}
              setBulkUnit={setBulkUnit}
              bulkValue={bulkValue}
              setBulkValue={setBulkValue}
              onApplyBulk={onApplyBulk}
              belowCostCount={belowCostCount}
            />
          </div>
        )}

        {errorMessage && (
          <p className="mt-3 text-xs text-danger">{errorMessage}</p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={onConfirm}
            disabled={
              isSubmitting || connections.length === 0 || !selectedConnectionId
            }
          >
            {isSubmitting
              ? isRepublish
                ? 'Re-publishing…'
                : 'Publishing…'
              : isRepublish
                ? 'Re-publish'
                : 'Publish'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// BulkPricingToolbar
// -----------------------------------------------------------------------------
// Single-dropdown bulk pricing control shared by the merged variant tables and
// the publish dialog's pricing step:
//   [operation ▾] · [unit $/%] · [value] · [Apply] · {trailing}
// Operation drives the sign; unit drives amount-vs-percent. `set` is always a
// flat amount; `margin` is always a percentage of cost. All math lives in
// @tkpod/shared (adjustPrice) so every surface behaves identically.
// =============================================================================

const BULK_OP_LABELS: Record<PriceBulkOp, string> = {
  set: 'Set retail price to',
  increase: 'Increase retail price by',
  decrease: 'Decrease retail price by',
  margin: 'Set from cost + margin',
};

function BulkPricingToolbar({
  op,
  setOp,
  unit,
  setUnit,
  value,
  setValue,
  onApply,
  trailing,
}: {
  op: PriceBulkOp;
  setOp: (v: PriceBulkOp) => void;
  unit: PriceBulkUnit;
  setUnit: (v: PriceBulkUnit) => void;
  value: string;
  setValue: (v: string) => void;
  onApply: () => void;
  trailing?: ReactNode;
}) {
  // `set` forces $ ; `margin` forces % . Only increase/decrease toggle units.
  const unitLocked = op === 'set' || op === 'margin';
  const effectiveUnit: PriceBulkUnit =
    op === 'set' ? 'amount' : op === 'margin' ? 'percent' : unit;
  const selectCls =
    'rounded-md border border-border bg-surface px-2 py-1 text-sm text-text-primary focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30';
  const inputCls =
    'w-28 rounded-md border border-border bg-surface px-2 py-1 text-sm text-text-primary focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30';

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-surface-secondary/30 p-2.5">
      <select
        aria-label="Bulk pricing operation"
        value={op}
        onChange={(e) => setOp(e.target.value as PriceBulkOp)}
        className={selectCls}
      >
        {(Object.keys(BULK_OP_LABELS) as PriceBulkOp[]).map((k) => (
          <option key={k} value={k}>
            {BULK_OP_LABELS[k]}
          </option>
        ))}
      </select>
      <select
        aria-label="Bulk pricing unit"
        value={effectiveUnit}
        disabled={unitLocked}
        onChange={(e) => setUnit(e.target.value as PriceBulkUnit)}
        className={`${selectCls} ${unitLocked ? 'opacity-60' : ''}`}
      >
        <option value="amount">$</option>
        <option value="percent">%</option>
      </select>
      <input
        type="text"
        inputMode="decimal"
        aria-label="Bulk pricing value"
        value={value}
        placeholder={effectiveUnit === 'percent' ? 'e.g. 20' : '0.00'}
        onChange={(e) => setValue(e.target.value)}
        className={inputCls}
      />
      <Button variant="outline" size="sm" onClick={onApply}>
        Apply
      </Button>
      {trailing}
    </div>
  );
}

// -----------------------------------------------------------------------------
// EditablePriceInput / ProfitCell — small shared presentational pieces used by
// the merged variant tables (design + template) and the dialog's PricingEditor.
// -----------------------------------------------------------------------------

function EditablePriceInput({
  value,
  onChange,
  ariaLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
}) {
  return (
    <div className="flex items-center justify-end gap-1">
      <span className="text-text-tertiary">$</span>
      <input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel}
        className="w-24 rounded-md border border-border bg-surface px-2 py-1 text-right text-sm text-text-primary focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
      />
    </div>
  );
}

function ProfitCell({
  price,
  cost,
  cellClassName = 'px-5 py-2.5',
}: {
  price: string;
  cost: string | null;
  cellClassName?: string;
}) {
  const priceNum = parseMoneyInput(price);
  const costNum = cost != null ? Number.parseFloat(cost) : null;
  const profit =
    priceNum != null ? computeProfit(priceNum, costNum) : null;
  return (
    <td
      className={`${cellClassName} text-right font-medium ${
        profit == null
          ? 'text-text-tertiary'
          : profit >= 0
            ? 'text-success'
            : 'text-danger'
      }`}
    >
      {profit != null ? `$${profit.toFixed(2)}` : '—'}
    </td>
  );
}

// =============================================================================
// PricingEditor
// -----------------------------------------------------------------------------
// Compact editable pricing table used ONLY by the publish dialog's pricing
// step (the inline page now merges pricing into the variant tables). Bulk
// toolbar (single dropdown) on top of a per-variant editable selling price
// table with live profit + a non-blocking below-cost warning.
// =============================================================================

interface PricingEditorProps {
  variants: ProductVariantRow[];
  drafts: Record<string, string>;
  onChange: (variantId: string, value: string) => void;
  bulkOp: PriceBulkOp;
  setBulkOp: (v: PriceBulkOp) => void;
  bulkUnit: PriceBulkUnit;
  setBulkUnit: (v: PriceBulkUnit) => void;
  bulkValue: string;
  setBulkValue: (v: string) => void;
  onApplyBulk: () => void;
  belowCostCount: number;
}

function PricingEditor({
  variants,
  drafts,
  onChange,
  bulkOp,
  setBulkOp,
  bulkUnit,
  setBulkUnit,
  bulkValue,
  setBulkValue,
  onApplyBulk,
  belowCostCount,
}: PricingEditorProps) {
  const anyImage = variants.some((v) => v.imageUrl);

  return (
    <div className="space-y-3">
      <BulkPricingToolbar
        op={bulkOp}
        setOp={setBulkOp}
        unit={bulkUnit}
        setUnit={setBulkUnit}
        value={bulkValue}
        setValue={setBulkValue}
        onApply={onApplyBulk}
      />
      {belowCostCount > 0 && (
        <p className="text-xs text-warning">
          {belowCostCount === 1
            ? '1 variant is priced below its cost (selling at a loss).'
            : `${belowCostCount} variants are priced below their cost (selling at a loss).`}
        </p>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-surface-secondary text-[10px] uppercase tracking-wider text-text-tertiary">
            <tr>
              {anyImage && (
                <th className="px-3 py-2 text-left font-semibold w-14">Image</th>
              )}
              <th className="px-3 py-2 text-left font-semibold">Variant</th>
              <th className="px-3 py-2 text-left font-semibold">SKU</th>
              <th className="px-3 py-2 text-right font-semibold">Cost</th>
              <th className="px-3 py-2 text-right font-semibold">
                Your selling price
              </th>
              <th className="px-3 py-2 text-right font-semibold">Profit</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {variants.map((v) => {
              const draft = drafts[v.id] ?? v.price;
              const costNum = v.cost != null ? Number.parseFloat(v.cost) : null;
              return (
                <tr key={v.id} className="hover:bg-surface-secondary/50">
                  {anyImage && (
                    <td className="px-3 py-2">
                      {v.imageUrl ? (
                        <img
                          src={v.imageUrl}
                          alt={v.title ?? v.internalSku}
                          loading="lazy"
                          className="h-10 w-10 rounded border border-border object-cover bg-surface-secondary"
                        />
                      ) : (
                        <div className="h-10 w-10 rounded border border-border bg-surface-secondary" />
                      )}
                    </td>
                  )}
                  <td className="px-3 py-2 text-text-primary">
                    <span className="block truncate max-w-[12rem]">
                      {v.title ?? v.internalSku}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px] text-text-tertiary">
                    {v.externalSku ?? v.internalSku}
                  </td>
                  <td className="px-3 py-2 text-right text-text-tertiary">
                    {costNum != null ? `$${costNum.toFixed(2)}` : '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <EditablePriceInput
                      value={draft}
                      onChange={(val) => onChange(v.id, val)}
                      ariaLabel={`Selling price for ${v.title ?? v.internalSku}`}
                    />
                  </td>
                  <ProfitCell
                    price={draft}
                    cost={v.cost}
                    cellClassName="px-3 py-2"
                  />
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============================================================================
// DesignGeneratedVariantsTable
// -----------------------------------------------------------------------------
// Apparel multi-side v2 variant viewer. The main table always shows the
// variant composite hero (`ProductVariant.imageUrl` — Front + Back stitched
// when both sides have artwork). If the stitch is not ready yet, the cell
// falls back Front → Back → first READY side. Extra print areas live in the
// Preview gallery, never in extra tables.
//
// Polling is owned by the parent — this component is a pure derivation of
// `variants`, so React Query refetches re-render it for free.
// =============================================================================

function DesignGeneratedVariantsTable({
  variants,
  drafts,
  onPriceChange,
}: {
  variants: ProductVariantRow[];
  drafts: Record<string, string>;
  onPriceChange: (variantId: string, value: string) => void;
}) {
  const [bulkGroup, setBulkGroup] = useState<{
    sizeLabel: string;
    items: ProductVariantRow[];
  } | null>(null);

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-surface-secondary text-[10px] uppercase tracking-wider text-text-tertiary">
            <tr>
              <th className="px-3 py-2 text-left font-semibold w-20">
                Composite preview
              </th>
              <th className="px-3 py-2 text-left font-semibold">Variant</th>
              <th className="px-3 py-2 text-left font-semibold">SKU</th>
              <th className="px-3 py-2 text-right font-semibold">Cost</th>
              <th className="px-3 py-2 text-right font-semibold">
                Your selling price
              </th>
              <th className="px-3 py-2 text-right font-semibold">Profit</th>
              <th className="px-3 py-2 text-left font-semibold">Status</th>
            </tr>
          </thead>
          <GroupedVariantTable
            variants={variants}
            getOptions={(v) => v.options}
            colSpan={7}
            renderGroupActions={(group) => {
              const unique = [
                ...new Set(group.items.map((i) => drafts[i.id] ?? i.price)),
              ];
              return (
                <div className="flex flex-wrap items-center gap-1">
                  {unique.slice(0, 3).map((p) => (
                    <span
                      key={p}
                      className="rounded-full bg-surface px-2 py-0.5 text-[10px] font-medium text-text-secondary"
                    >
                      ${p}
                    </span>
                  ))}
                  {unique.length > 3 ? (
                    <span className="text-[10px] text-text-tertiary">
                      +{unique.length - 3}
                    </span>
                  ) : null}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setBulkGroup({
                        sizeLabel: group.sizeLabel,
                        items: group.items,
                      })
                    }
                  >
                    Edit bulk
                  </Button>
                </div>
              );
            }}
            renderRow={(v) => {
              const { url, status } = resolveTableHeroPreview(v);
              const label = variantRowLabel({
                title: v.title,
                options: v.options,
              });
              return (
                <tr key={v.id} className="hover:bg-surface-secondary/50">
                  <td className="px-3 py-2">
                    <div className="relative h-14 w-14 overflow-hidden rounded border border-border bg-surface-secondary">
                      {url ? (
                        <img
                          src={url}
                          alt={`${label} composite preview`}
                          className="h-full w-full object-contain"
                          loading="lazy"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-[10px] text-text-tertiary">
                          {status === 'PENDING' ||
                          status === 'QUEUED' ||
                          status === 'RUNNING' ? (
                            <Spinner size="sm" />
                          ) : status === 'FAILED' ? (
                            '✕'
                          ) : (
                            '—'
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-text-primary">
                    <span className="block truncate max-w-[14rem]">{label}</span>
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px] text-text-tertiary">
                    {v.externalSku ?? v.internalSku}
                  </td>
                  <td className="px-3 py-2 text-right text-text-tertiary">
                    {v.cost ? `$${Number.parseFloat(v.cost).toFixed(2)}` : '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <EditablePriceInput
                        value={drafts[v.id] ?? v.price}
                        onChange={(val) => onPriceChange(v.id, val)}
                        ariaLabel={`Selling price for ${label}`}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const sizeKey =
                            optionValueByAxisHints(v.options, SIZE_AXIS_HINTS)?.toLowerCase() ??
                            '';
                          const items = variants.filter(
                            (other) =>
                              (optionValueByAxisHints(
                                other.options,
                                SIZE_AXIS_HINTS,
                              )?.toLowerCase() ?? '') === sizeKey,
                          );
                          setBulkGroup({
                            sizeLabel:
                              optionValueByAxisHints(v.options, SIZE_AXIS_HINTS) ??
                              'Ungrouped',
                            items,
                          });
                        }}
                      >
                        Edit bulk
                      </Button>
                    </div>
                  </td>
                  <ProfitCell
                    price={drafts[v.id] ?? v.price}
                    cost={v.cost}
                    cellClassName="px-3 py-2"
                  />
                  <td className="px-3 py-2">
                    <Badge
                      variant={
                        PREVIEW_JOB_VARIANT[v.previewJobStatus] ?? 'default'
                      }
                      size="sm"
                    >
                      {v.previewJobStatus}
                    </Badge>
                  </td>
                </tr>
              );
            }}
          />
        </table>
      </div>
      <VariantBulkPriceModal
        open={bulkGroup != null}
        onClose={() => setBulkGroup(null)}
        sizeLabel={bulkGroup?.sizeLabel ?? ''}
        items={bulkGroup?.items ?? []}
        drafts={drafts}
        onApply={(next) => {
          for (const [id, val] of Object.entries(next)) {
            onPriceChange(id, val);
          }
        }}
      />
    </div>
  );
}
